import { useLocalStorage } from '@shared/utils/useStorage';
import type {
  ExpandedState,
  RowData,
  RowSelectionState,
} from '@tanstack/react-table';
import { useTable } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { StateCleanup } from './components/StateCleanup';
import { Context, type DataProviderContext } from './DataProviderContext';
import { createTableFeatures } from './features';
import { DefaultColumnFilter } from './filter/DefaultColumnFilter';
import { FilterProvider } from './filter/FilterContext';
import { useTableState } from './hooks/useTableState';
import type { ResolvedColumnDef } from './internal/getTableColumnDef';
import type {
  TableFilterFn,
  TableOptions,
  TableRow,
  TableState,
} from './tanstackTypes';
import type { ExtendedColumnDef, TableAction, TableFeatures } from './types';
import { useExtendedColumnDefs } from './useExtendedColumnDefs';

type StructuredRowData = RowData & {
  subRows?: StructuredRowData[];
};

export interface BaseDataProviderProps<TData extends RowData, TTexts = never> {
  children?: ReactNode;
  columnDefs: ExtendedColumnDef<TData, TTexts>[];
  initialData: TData[];
  initialState?: Partial<TableState>;
  actions?: TableAction[];
  isLoading?: boolean;
  isRowIndeterminate?: (row: TableRow<TData>) => boolean;
  isRowHighlighted?: (row: TableRow<TData>) => boolean;
  isRowClickable?: (row: TableRow<TData>) => boolean;
  onRowClick?: (row: TableRow<TData>) => void;
  toolbarControls?: ReactNode;
  id?: string;
}

export interface ClientSideDataProviderProps<
  TData extends RowData,
  TTexts = never,
> extends BaseDataProviderProps<TData, TTexts> {
  /**
   * Capabilities registered when the table is created. These must remain
   * stable for the lifetime of the table instance.
   */
  features?: TableFeatures;
  enableFilters?: boolean;
  enableGlobalFiltering?: boolean;
  globalFilterFn?: TableFilterFn<TData>;
  enableGrouping?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableStructure?: boolean;
  toStructure?: (flatData: TData[]) => StructuredRowData[];
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  getRowId?: (
    originalRow: TData,
    index: number,
    parent?: TableRow<TData>
  ) => string;
}

export const DataProvider = <TData extends RowData, TTexts = never>({
  children,
  columnDefs,
  initialData,
  initialState,
  actions = [],
  features: registeredFeatures = {},
  enableFilters = false,
  enableGlobalFiltering = false,
  globalFilterFn,
  enableGrouping = false,
  enableSorting = false,
  enablePagination = false,
  enableStructure = false,
  toStructure,
  isLoading = false,
  isRowIndeterminate,
  isRowClickable,
  isRowHighlighted,
  onRowClick,
  toolbarControls,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  id,
}: ClientSideDataProviderProps<TData, TTexts>) => {
  const extendedColumnDefs = useExtendedColumnDefs<TData, TTexts>(columnDefs);
  const { tableState, updateTableState } = useTableState(id, initialState);
  const [expanded, setExpanded] = useLocalStorage<ExpandedState>(
    [id, 'expanded'].join('.'),
    {}
  );
  const data = useMemo(
    () =>
      enableStructure && typeof toStructure === 'function'
        ? toStructure(initialData)
        : initialData,
    [enableStructure, toStructure, initialData]
  );
  const state: Partial<TableState> = {
    ...tableState,
  };
  const defaultColumn: Partial<ResolvedColumnDef<TData, TTexts>> = {
    Filter: DefaultColumnFilter,
  };
  const enableRowSelection = Boolean(rowSelection && onRowSelectionChange);
  const tableFeatures = useMemo(
    () =>
      createTableFeatures({
        enableFilters: enableFilters || registeredFeatures.filtering,
        enableGlobalFiltering:
          enableGlobalFiltering || registeredFeatures.globalFiltering,
        enableGrouping: enableGrouping || registeredFeatures.grouping,
        enableSorting: enableSorting || registeredFeatures.sorting,
        enablePagination: enablePagination || registeredFeatures.pagination,
        enableStructure: enableStructure || registeredFeatures.structure,
        enableRowSelection:
          enableRowSelection || registeredFeatures.rowSelection,
      }),
    [
      enableFilters,
      enableGlobalFiltering,
      enableGrouping,
      enableSorting,
      enablePagination,
      enableStructure,
      enableRowSelection,
      registeredFeatures.filtering,
      registeredFeatures.globalFiltering,
      registeredFeatures.grouping,
      registeredFeatures.sorting,
      registeredFeatures.pagination,
      registeredFeatures.structure,
      registeredFeatures.rowSelection,
    ]
  );

  const options: TableOptions<TData> = {
    features: tableFeatures,
    columns: extendedColumnDefs,
    data: data as TData[],
    state,
    // Used by auto-reset (e.g. autoResetPageIndex) to restore state after data changes.
    // initialState is also merged into the controlled state so it takes effect on first render.
    initialState,
    enableSorting: false,
    enableFilters: false,
    enableGrouping: false,
    enableRowSelection: false,
    defaultColumn,
    onColumnFiltersChange: (updater) =>
      updateTableState('columnFilters', updater),
    onColumnOrderChange: (updater) => updateTableState('columnOrder', updater),
    onColumnVisibilityChange: (updater) =>
      updateTableState('columnVisibility', updater),
    onExpandedChange: (updater) => updateTableState('expanded', updater),
    onGlobalFilterChange: (updater) =>
      updateTableState('globalFilter', updater),
    onGroupingChange: (updater) => updateTableState('grouping', updater),
    onPaginationChange: (updater) => updateTableState('pagination', updater),
    onSortingChange: (updater) => updateTableState('sorting', updater),
  };

  if (enableSorting) {
    options.enableSorting = true;
  }

  if (enableFilters) {
    options.enableFilters = true;
    options.filterFromLeafRows = false;
  }

  if (enableGlobalFiltering) {
    options.enableFilters = true;
    options.enableGlobalFilter = true;
    if (globalFilterFn) {
      options.globalFilterFn = globalFilterFn;
    }
  }

  if (enableGrouping) {
    options.enableGrouping = true;
  }

  if (enableStructure) {
    state.expanded = expanded;
    options.onExpandedChange = (updater) => {
      const next = typeof updater === 'function' ? updater(expanded) : updater;
      setExpanded(next);
    };
    options.getSubRows = ((row: StructuredRowData) =>
      row.subRows) as NonNullable<TableOptions<TData>['getSubRows']>;
    options.filterFromLeafRows = true;
  }

  if (enableRowSelection && rowSelection && onRowSelectionChange) {
    options.enableRowSelection = true;
    state.rowSelection = rowSelection;
    options.onRowSelectionChange = (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      onRowSelectionChange(next);
    };
    if (getRowId) {
      options.getRowId = getRowId;
    }
  }

  const table = useTable(options);
  const getRowCount = () => {
    if (
      enablePagination &&
      typeof table.getPrePaginatedRowModel === 'function'
    ) {
      return table.getPrePaginatedRowModel().rows.length;
    }

    if (
      (enableGrouping || enableStructure) &&
      typeof table.getExpandedRowModel === 'function'
    ) {
      return table.getExpandedRowModel().rows.length;
    }

    return table.getRowModel().rows.length;
  };

  return (
    <Context.Provider
      value={
        {
          table,
          actions,
          toolbarControls,
          isLoading,
          isRowIndeterminate,
          isRowClickable,
          isRowHighlighted,
          onRowClick,
          getRowCount,
          isPaginationEnabled: enablePagination,
          id,
        } as DataProviderContext<TData>
      }
    >
      <FilterProvider>
        <StateCleanup />
        {children}
      </FilterProvider>
    </Context.Provider>
  );
};
